import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TabSpec, SectionSpec, ListItem } from '../../../core/interfaces/report-spec.interface';
import { PDFDocument } from '../../../core/interfaces/pdf-document.interface';
import { UniversalPdfRendererService } from '../../../core/services/universal-pdf-renderer.service';
import { MarkdownRendererComponent } from '../markdown-renderer/markdown-renderer.component';

/**
 * Universal Report Renderer Component
 *
 * Renders a single tab (with its nested sections) to HTML for web display.
 * This is a declarative renderer that interprets the tab spec format
 * and generates the corresponding HTML output.
 *
 * Features:
 * - Renders 4 universal component types: text, list, card, table
 * - Composable (cards can contain any sections recursively)
 * - Smart list rendering (adapts to item types)
 * - Flat rendering (PDF-friendly)
 *
 * Usage:
 * <app-universal-report-renderer [tab]="tabSpec"></app-universal-report-renderer>
 */
@Component({
  selector: 'app-universal-report-renderer',
  standalone: true,
  imports: [CommonModule, MarkdownRendererComponent],
  template: `
    <div class="report-container py-6" *ngIf="tab">
      <ng-container *ngFor="let section of tab.sections; let isLast = last">
        <div class="section-wrapper mb-8">
          <ng-container *ngTemplateOutlet="sectionTemplate; context: { section: section }"></ng-container>
        </div>
      </ng-container>
    </div>

    <!-- Recursive Section Template -->
    <ng-template #sectionTemplate let-section="section">

      <!-- Text Type -->
      <div *ngIf="section.type === 'text'" class="text-section">
        <div
          [ngClass]="{
            'text-3xl font-semibold mb-6 text-gray-900': section.variant === 'title',
            'text-xl font-semibold mb-4 text-gray-900': section.variant === 'heading',
            'text-base mb-3 text-gray-800': section.variant === 'body' || !section.variant
          }">
          <app-markdown-renderer [text]="section.content"></app-markdown-renderer>
        </div>
      </div>

      <!-- List Type -->
      <div *ngIf="section.type === 'list'" class="list-section">
        <!-- Smart rendering based on item types -->
        <ng-container *ngIf="isFieldList(section.items); else bulletList">
          <!-- Definition list for labeled fields -->
          <div class="w-full">
            <table class="min-w-full divide-y divide-gray-200">
              <tbody class="bg-white divide-y divide-gray-200">
                <tr *ngFor="let item of section.items" class="hover:bg-gray-50 transition-colors">
                  <td class="py-3 px-4 text-base font-medium text-gray-900">{{ getItemLabel(item) }}</td>
                  <td class="py-3 px-4 text-base text-gray-800" style="white-space: pre-line">
                    {{ getItemValue(item) }}
                    <span *ngIf="getItemMetadata(item)" class="ml-2 text-sm text-gray-500">
                      {{ getItemMetadata(item) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ng-container>
        <ng-template #bulletList>
          <!-- Bullet list for simple strings -->
          <ul class="list-disc list-inside space-y-2 text-gray-800">
            <li *ngFor="let item of section.items" class="text-base">
              {{ getItemValue(item) }}
              <span *ngIf="getItemMetadata(item)" class="ml-2 text-sm text-gray-500">
                {{ getItemMetadata(item) }}
              </span>
            </li>
          </ul>
        </ng-template>
      </div>

      <!-- Card Type -->
      <div *ngIf="section.type === 'card'" class="card-section mb-6">
        <div class="border border-gray-400 rounded-lg overflow-hidden bg-white">
          <table class="min-w-full">
            <thead *ngIf="section.title">
              <tr class="bg-gray-100 border-b border-gray-400">
                <th colspan="2" class="py-3 px-4 text-left">
                  <h2 class="text-lg font-semibold text-gray-900 m-0">{{ section.title }}</h2>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="2" class="p-6">
                  <!-- Recursively render child content -->
                  <ng-container *ngFor="let child of section.content; let isChildLast = last">
                    <ng-container *ngTemplateOutlet="sectionTemplate; context: { section: child }"></ng-container>
                    <div *ngIf="!isChildLast" class="my-6 border-t border-gray-300"></div>
                  </ng-container>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Table Type -->
      <div *ngIf="section.type === 'table'" class="table-section mb-6">
        <div class="border border-gray-400 rounded-lg overflow-hidden bg-white">
          <table class="min-w-full divide-y divide-gray-200">
            <thead>
              <tr *ngIf="section.title" class="bg-gray-100 border-b border-gray-400">
                <th [attr.colspan]="section.columns.length" class="py-3 px-4 text-left">
                  <h2 class="text-lg font-semibold text-gray-900 m-0">{{ section.title }}</h2>
                </th>
              </tr>
              <tr class="bg-gray-100" *ngIf="section.columns[0] !== ''">
                <th *ngFor="let col of section.columns" class="py-3 px-4 text-left text-sm font-semibold text-gray-900">
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let row of section.rows" class="hover:bg-gray-50 transition-colors">
                <td *ngFor="let cell of row; let i = index"
                    class="py-3 px-4 text-base"
                    [ngClass]="i === 0 ? 'font-medium text-gray-900' : 'text-gray-800'">
                  {{ cell }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </ng-template>
  `,
  styles: [`
    :host {
      display: block;
    }

    .section-wrapper {
      page-break-inside: avoid;
    }

    @media print {
      .section-wrapper {
        page-break-inside: avoid;
      }
    }
  `]
})
export class UniversalReportRendererComponent {
  @Input() tab!: TabSpec;
  @Input() printMode: boolean = false;

  /**
   * Check if list items should be rendered as a field list (definition list)
   * Returns true if any item has a label property
   */
  isFieldList(items: ListItem[]): boolean {
    return items.some(item =>
      typeof item !== 'string' && item.label !== undefined
    );
  }

  /**
   * Helper to get label from list item
   */
  getItemLabel(item: ListItem): string {
    return typeof item === 'string' ? '' : (item.label || '');
  }

  /**
   * Helper to get value from list item
   */
  getItemValue(item: ListItem): string {
    return typeof item === 'string' ? item : item.value;
  }

  /**
   * Helper to get metadata from list item
   */
  getItemMetadata(item: ListItem): string | undefined {
    return typeof item === 'string' ? undefined : item.metadata;
  }

  /**
   * Static PDF renderer - called by pdf.service.ts
   * This method is invoked when generating PDFs
   *
   * @param doc - PDFKit document instance
   * @param data - Report data (includes tab)
   * @param dueDiligence - Original diligence data (not used for spec-based reports)
   */
  static renderToPdf(doc: PDFDocument, data: any, dueDiligence: any): void {
    const pdfRenderer = new UniversalPdfRendererService();
    pdfRenderer.renderTab(doc, data.tab);
  }
}
